// États de facture (ID_ETAT_DECLENCHEMENT).
//
// Source unique partagée par la table desktop (InvoiceListPage), les cartes
// mobiles (InvoiceMobileCards) et l'écran admin (routes/admin/Invoices) pour
// éviter toute redivergence.
//
// ⚠️ Piège de vocabulaire : « impayé » désigne TROIS états selon d'où on
// regarde. L'état 2 porte le libellé « Impayé » dans la table de référence
// comptable (visible dans Symfony, colonne « État compta (Quadratus) ») alors
// qu'il s'affiche « En cours » ici — c'est simplement l'état de toute facture
// fraîchement émise, il ne signale aucune anomalie. Les états 5 et 6 sont les
// vrais impayés, respectivement définitif et en cours de relance.

export const INVOICE_STATE = {
  PAID: 1,
  /** Émise, en attente d'encaissement. Seul état (avec REJECTED) prélevé en SEPA. */
  PENDING: 2,
  REJECTED: 4,
  /** Impayé définitif : ne sera jamais encaissé, perte actée. */
  UNPAID: 5,
  /** Impayé en cours de relance : encaissement encore espéré. */
  UNPAID_IN_PROGRESS: 6,
} as const;

export type InvoiceStateValue =
  (typeof INVOICE_STATE)[keyof typeof INVOICE_STATE];

export const INVOICE_STATES: InvoiceStateValue[] = [
  INVOICE_STATE.PAID,
  INVOICE_STATE.PENDING,
  INVOICE_STATE.REJECTED,
  INVOICE_STATE.UNPAID_IN_PROGRESS,
  INVOICE_STATE.UNPAID,
];

export function getInvoiceStateLabel(state: number): string {
  switch (state) {
    case INVOICE_STATE.PAID: return 'Payée';
    case INVOICE_STATE.PENDING: return 'En cours';
    case INVOICE_STATE.REJECTED: return 'Rejetée';
    case INVOICE_STATE.UNPAID: return 'Impayée';
    case INVOICE_STATE.UNPAID_IN_PROGRESS: return 'Impayé en cours';
    default: return 'En attente';
  }
}

/**
 * Libellés de l'écran ADMIN (onglets d'état, aperçu de facturation), plus
 * précis sur le recouvrement que ceux montrés aux vendeurs. Les écrans vendeur
 * gardent `getInvoiceStateLabel` : ne pas fusionner les deux.
 */
const ADMIN_INVOICE_STATE_LABELS: Record<InvoiceStateValue, string> = {
  [INVOICE_STATE.PAID]: 'Payée',
  [INVOICE_STATE.PENDING]: 'Virement en attente',
  [INVOICE_STATE.REJECTED]: 'Paiement rejeté / reprélevé M+1',
  [INVOICE_STATE.UNPAID_IN_PROGRESS]: 'Impayé en cours',
  [INVOICE_STATE.UNPAID]: 'Impayé constaté',
};

export function getAdminInvoiceStateLabel(state: number): string {
  return ADMIN_INVOICE_STATE_LABELS[state as InvoiceStateValue] ?? getInvoiceStateLabel(state);
}

export function getInvoiceStateColorClass(state: number): string {
  switch (state) {
    case INVOICE_STATE.PAID: return 'tw-bg-green-100 tw-text-green-700';   // payée
    case INVOICE_STATE.PENDING: return 'tw-bg-blue-100 tw-text-blue-700';  // en cours
    case INVOICE_STATE.REJECTED: return 'tw-bg-red-100 tw-text-red-700';   // rejetée
    // Ambre pour la relance en cours, orange pour la perte actée : la nuance
    // dit qu'on attend encore quelque chose du premier et plus rien du second.
    case INVOICE_STATE.UNPAID_IN_PROGRESS: return 'tw-bg-amber-100 tw-text-amber-700';
    case INVOICE_STATE.UNPAID: return 'tw-bg-orange-100 tw-text-orange-700';
    default: return 'tw-bg-gray-100 tw-text-gray-700';
  }
}
